import React, { useEffect, useRef, useState, useCallback } from "react";
import Lottie from "lottie-react";
import {
  startSession,
  endSession,
  getContentRound,
  getGameId,
  getMyGameSettings,
} from "../../services/GameService";
import { speak, isSpeechSupported } from "../../utils/speech";
import GameOverScreen from "../GameOverScreen";
import EdgeFlowers from "../EdgeFlowers";
import { usePlayClock } from "../common/usePlayClock";
import fireworksData from "./animations/fireworks.json";
import fireworksSound from "./sounds/fireworks.mp3";
import correctSound from "./sounds/correct.mp3";
import errorSound from "./sounds/error.mp3";
import "./MatchingGameForm.css";

const DEFAULT_ROUNDS = 5;
const DEFAULT_OPTIONS = 4;

// Pull a usable option count out of a level's level_parameters JSON. Levels
// store their parameters as a JSON string (e.g. '{"num_options": 5}'); higher
// levels carry a bigger num_options so more pictures/words compete each round
// (#117). Anything missing or malformed yields null so the caller keeps its
// current value.
export const parseLevelNumOptions = (level) => {
  if (!level || !level.level_parameters) return null;
  try {
    const params = JSON.parse(level.level_parameters);
    const n = params && params.num_options;
    return Number.isFinite(n) && n >= 2 ? n : null;
  } catch (e) {
    return null;
  }
};

// Pull the number of rounds per game out of a level's level_parameters JSON
// (e.g. '{"num_options": 3, "rounds": 8}'). More rounds make each score step
// smaller, so promotion depends less on a lucky guess. Anything missing,
// malformed or out of range (1-20) yields null so the caller keeps the default.
export const parseLevelRounds = (level) => {
  if (!level || !level.level_parameters) return null;
  try {
    const params = JSON.parse(level.level_parameters);
    const n = params && params.rounds;
    return Number.isInteger(n) && n >= 1 && n <= 20 ? n : null;
  } catch (e) {
    return null;
  }
};

// Shared matching game for image→word, word→image, and voice→image (#38/#39/#41).
// `mode` decides whether the target is the image or the word; `speakTarget`
// reads the target word aloud (Voice → Image), showing text only as a fallback.
const MatchingGameForm = ({ mode, gameRoutePath, title, speakTarget }) => {
  const isImageToWord = mode === "image-to-word";

  const [round, setRound] = useState(null);
  const [roundNumber, setRoundNumber] = useState(0);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasPlayed, setHasPlayed] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [totalRounds, setTotalRounds] = useState(DEFAULT_ROUNDS);

  const sessionIdRef = useRef(null);
  const numOptionsRef = useRef(DEFAULT_OPTIONS);
  const totalRoundsRef = useRef(DEFAULT_ROUNDS);
  const scoreRef = useRef(0);
  const playClock = usePlayClock();
  const fireworksAudioRef = useRef(new Audio(fireworksSound));
  // Success / failure cues — the same sounds the Time Match games use.
  const correctSoundRef = useRef(new Audio(correctSound));
  const errorSoundRef = useRef(new Audio(errorSound));

  const loadRound = useCallback(async () => {
    setSelected(null);
    try {
      const data = await getContentRound({ options: numOptionsRef.current });
      setRound(data);
    } catch (e) {
      setError("Could not load a round. Add content in the admin first.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const session = await startSession(gameRoutePath);
        if (session && session.sessionId) {
          sessionIdRef.current = session.sessionId;
        }
        // Higher levels show more options to choose from (#117): the player's
        // current level (carried on the session) sets the option count, falling
        // back to the default when the level has no num_options.
        const levelOptions = parseLevelNumOptions(session && session.level);
        if (levelOptions) numOptionsRef.current = levelOptions;
        const levelRounds = parseLevelRounds(session && session.level);
        if (levelRounds) {
          totalRoundsRef.current = levelRounds;
          if (active) setTotalRounds(levelRounds);
        }

        const gameId = await getGameId(gameRoutePath);
        if (gameId) {
          const s = await getMyGameSettings(gameId);
          const n = s && s.settings && s.settings.num_options;
          // A clinician's explicit per-patient setting overrides the level.
          if (Number.isFinite(n) && n >= 2) numOptionsRef.current = n;
        }
      } catch (e) {
        // fall back to defaults
      }
      if (active) await loadRound();
      // Play starts with the first round on screen.
      if (active) playClock.start();
    })();
    return () => {
      active = false;
      // Mirror FindMiddle's audio cleanup so the celebration sound never
      // outlives the component.
      if (fireworksAudioRef.current) {
        fireworksAudioRef.current.pause();
        fireworksAudioRef.current.currentTime = 0;
      }
      [correctSoundRef, errorSoundRef].forEach((ref) => {
        if (ref.current) {
          ref.current.pause();
          ref.current.currentTime = 0;
        }
      });
    };
  }, [gameRoutePath, loadRound, playClock]);

  // Play the celebration sound while the fireworks show, and stop it on
  // cleanup. Mirrors FindMiddle's fireworks audio handling.
  useEffect(() => {
    const audio = fireworksAudioRef.current;
    if (showFireworks) {
      audio.loop = true;
      audio.play().catch((error) => {
        console.error("Failed to play fireworks sound:", error);
      });
    } else {
      audio.pause();
      audio.currentTime = 0;
    }
    return () => {
      if (showFireworks) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, [showFireworks]);

  // Voice → Image: read the target word aloud whenever a new round loads.
  useEffect(() => {
    if (speakTarget && round && round.target) {
      speak(round.target.word);
      // Each new word starts as "Play"; it becomes "Replay" once tapped.
      setHasPlayed(false);
    }
  }, [speakTarget, round]);

  const handleSelect = (option) => {
    if (selected) return;
    const correct = option.id === round.target.id;
    setSelected({ id: option.id, correct });
    // Play the success / failure cue, mirroring the Time Match games. Reset to
    // the start first so rapid rounds always replay from the top.
    const cue = correct ? correctSoundRef.current : errorSoundRef.current;
    if (cue) {
      cue.currentTime = 0;
      cue.play().catch((e) => console.error("Failed to play sound:", e));
    }
    if (correct) {
      scoreRef.current += 1;
      setScore(scoreRef.current);
    }
    // Last answer in: play time stops here, not after the closing pause.
    if (roundNumber + 1 >= totalRoundsRef.current) playClock.stop();
    setTimeout(() => {
      const next = roundNumber + 1;
      if (next >= totalRoundsRef.current) {
        setGameOver(true);
        setShowFireworks(true);
        endSession(
          sessionIdRef.current,
          (scoreRef.current / totalRoundsRef.current) * 100,
          playClock.elapsedMs()
        );
      } else {
        setRoundNumber(next);
        loadRound();
      }
    }, 1200);
  };

  if (loading) {
    return (
      <div className="matching-game">
        <p>Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="matching-game">
        <p className="mg-error">{error}</p>
      </div>
    );
  }
  if (gameOver) {
    return (
      <GameOverScreen
        stats={[`You got ${score} of ${totalRounds} correct!`]}
        showFireworks={showFireworks}
        fireworksAnimation={fireworksData}
      />
    );
  }
  if (!round) return null;

  return (
    <div className="matching-game">
      <EdgeFlowers />
      <h1 className="mg-title">{title}</h1>
      <p className="mg-progress">
        Round {roundNumber + 1} of {totalRounds}
      </p>

      <div className="mg-target">
        {isImageToWord ? (
          <img src={round.target.image} alt="" className="mg-target-image" />
        ) : speakTarget && isSpeechSupported() ? (
          <button
            type="button"
            className="mg-replay"
            onClick={() => {
              speak(round.target.word);
              setHasPlayed(true);
            }}
          >
            🔊 {hasPlayed ? "Replay" : "Play"}
          </button>
        ) : (
          <span className="mg-target-word">{round.target.word}</span>
        )}
      </div>

      <div
        className={`mg-options ${
          isImageToWord ? "mg-options-words" : "mg-options-images"
        }`}
      >
        {round.options.map((opt) => {
          const isSel = selected && selected.id === opt.id;
          const stateClass = isSel
            ? selected.correct
              ? "mg-correct"
              : "mg-incorrect"
            : "";
          return (
            <button
              key={opt.id}
              className={`mg-option ${stateClass}`}
              onClick={() => handleSelect(opt)}
              disabled={Boolean(selected)}
            >
              {isImageToWord ? (
                opt.word
              ) : (
                <img src={opt.image} alt={opt.word} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MatchingGameForm;
